/**
 * Freeform PDP cover selector grid (FSD §1.4 / §5.3).
 *
 * - Visible slots by breakpoint (includes "+N more" when overflowing):
 *   base (<375): 6; xsm+: 8. Theme columns: 3 / 4 / 8 / 4 (base / xsm / md / lg).
 * - Per-tile: image + name (+ optional description), stock badge, stock message, and
 *   diagonal overlay for Sold-out items. Configurable badge (if any) takes precedence
 *   over the stock badge.
 * - Click handling: ALL tiles (in-stock AND out-of-stock) commit the cover selection.
 *   Selecting an OOS cover swaps the ATC CTA to "Notify Me" at the PDP level (see
 *   `AddToCartStrip` — `freeformIsNotifyMeMode` reacts to the selected variant's
 *   stock state). FSD §1.3 / §5.1 — OOS variants are intentionally selectable so
 *   the user can register interest via Notify Me.
 */

import {useCallback, useEffect, useId, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getCoverVariantLabelLines} from '@amblique/king-living/app/components/complex-customize/option-step/product-picker/cover-variant-tile';
import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';
import CoverSwatchImageNotAvailable from '@amblique/king-living/app/components/cover-swatch-image-not-available';
import {ColorPaletteSampleIcon} from '@amblique/king-living/app/components/cover-swatch-grid/color-palette-sample-icon';
import {ChevronRightIcon2} from '@amblique/king-living/app/components/icons';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getCoverExtraPrice, getCoverStockMessageClassName, getCoverStockMessageStyleKey, getStockLevel} from '@amblique/king-living/app/utils/cover-stock-display';
import {sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';

const DEFAULT_VISIBLE_TILE_CAP = 8;
const MORE_CTA_RESERVED_SLOTS = 1; // the last tile is replaced by `+N More` when overflowing
const VISIBLE_TILE_CAP_BY_BREAKPOINT = {base: 6, xsm: 8};

function getCoverVariantItemType(variant) {
  const processed = variant?.c_processedData ?? {};
  const itemType = (processed.itemType ?? variant?.c_itemType ?? '').trim();

  return itemType || undefined;
}

function getCoverViewDetailsOptions(variant) {
  const coverItemType = getCoverVariantItemType(variant);

  return coverItemType ? {coverItemType} : {};
}

/**
 * Builds the visible swatch slice (overflow last tile is "+N more").
 * When the committed selection lives in the overflow bucket, promote it to slot 1
 * so the right-sidebar grid reflects the flyout choice.
 *
 * @param {Array} variants
 * @param {string|null|undefined} selectedCoverId
 * @param {number} [visibleTileCap]
 * @returns {{ visibleVariants: Array, moreCount: number }}
 */
export function getCoverSwatchGridVisibleVariants(
  variants,
  selectedCoverId,
  visibleTileCap = DEFAULT_VISIBLE_TILE_CAP
) {
  const list = Array.isArray(variants) ? variants : [];

  if (list.length <= visibleTileCap) {
    return {visibleVariants: list, moreCount: 0};
  }

  const remainingSlots = visibleTileCap - MORE_CTA_RESERVED_SLOTS;
  const defaultVisible = list.slice(0, remainingSlots);
  const moreCount = list.length - remainingSlots;

  if (!selectedCoverId) {
    return {visibleVariants: defaultVisible, moreCount};
  }

  const selectedIndex = list.findIndex((variant) => variant.productId === selectedCoverId);

  if (selectedIndex < 0 || selectedIndex < remainingSlots) {
    return {visibleVariants: defaultVisible, moreCount};
  }

  const selectedVariant = list[selectedIndex];
  const withoutSelected = list.filter((variant) => variant.productId !== selectedCoverId);

  return {
    visibleVariants: [selectedVariant, ...withoutSelected.slice(0, remainingSlots - 1)],
    moreCount,
  };
}

function CoverTile({variant, isSelected, isMobile, styles, masterMinPrice, currency, onSelect, onViewDetails, hideStockLabels = false}) {
  const intl = useIntl();
  // Replacement-carousel masters: no swatch stock labels — show price rule like perpetual.
  const stockLevel = hideStockLabels ? 'perpetual' : getStockLevel(variant);
  const isOutOfStock = stockLevel === 'oos';
  const isPerpetual = stockLevel === 'perpetual';
  const tileStyles = useMultiStyleConfig('CoverSwatchGrid', {isSelected, isOutOfStock});

  const {
    line1: nameLine1,
    line2: nameLine2,
    ariaLabel: fallbackLabel,
  } = getCoverVariantLabelLines(variant);

  const configurableBadge = variant.c_processedData?.badgeText || null;

  // Perpetual (made-to-order) variants: show the cover price rule in place of the stock message —
  // "+ {delta}" over the cheapest variant, or "Included" when there is no surcharge.
  const coverExtraPrice = isPerpetual ? getCoverExtraPrice(variant, masterMinPrice) : null;

  // Stock message copy (FSD §1.4). Perpetual variants swap the stock message for the price rule.
  // Low stock count from linked FG ATS when present.
  const atsForMessage =
    (typeof variant?.linkedInventory?.ats === 'number' ? variant.linkedInventory.ats : null) ??
    variant.ats ??
    0;

  let stockMessageText = null;
  let stockMessageStyleKey = getCoverStockMessageStyleKey(stockLevel);

  if (isPerpetual) {
    stockMessageText = coverExtraPrice != null ?
      `+ ${formatCurrencyHideTrailingZeroCents(intl, coverExtraPrice, currency ?? 'USD')}` :
      intl.formatMessage({
        defaultMessage: 'Included',
        id: 'cover_swatch_grid.price.included',
      });
  } else if (stockLevel === 'in-stock') {
    stockMessageText = intl.formatMessage({
      defaultMessage: 'Available',
      id: 'cover_swatch_grid.stock.available',
    });
  } else if (stockLevel === 'low-stock') {
    stockMessageText = intl.formatMessage(
      {
        defaultMessage: 'Only {count, plural, one {# left} other {# left}}',
        id: 'cover_swatch_grid.stock.only_n_left',
      },
      {count: atsForMessage}
    );
  } else {
    stockMessageText = intl.formatMessage({
      defaultMessage: 'Sold out',
      id: 'cover_swatch_grid.stock.sold_out',
    });
  }

  const stockMessageClassName = getCoverStockMessageClassName(stockLevel);

  // Badge: configurable badge always takes precedence. Perpetual variants show NO stock badge
  // (the price rule replaces it); stock-tracked variants keep In stock / Low stock.
  let badgeNode = null;

  if (configurableBadge) {
    badgeNode = (
      <Text {...tileStyles.tileConfigurableBadge} as="span">
        {configurableBadge}
      </Text>
    );
  } else if (isPerpetual) {
    badgeNode = null;
  } else if (stockLevel === 'in-stock') {
    badgeNode = (
      <Text {...tileStyles.tileStockBadge} as="span">
        <FormattedMessage
          defaultMessage="In stock"
          id="cover_swatch_grid.badge.in_stock"
        />
      </Text>
    );
  } else if (stockLevel === 'low-stock') {
    badgeNode = (
      <Text {...tileStyles.tileStockBadgeLow} as="span">
        <FormattedMessage
          defaultMessage="Low stock"
          id="cover_swatch_grid.badge.low_stock"
        />
      </Text>
    );
  }

  const handleClick = () => {
    onSelect?.(isSelected ? null : variant);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(variant, getCoverViewDetailsOptions(variant));
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    handleClick();
  };

  return (
    <Box
      {...tileStyles.tile}
      aria-checked={isSelected}
      aria-label={fallbackLabel}
      className="cover-swatch-grid__tile"
      data-testid="cover-swatch-grid-tile"
      {...sfccProductIdDataAttrs('variant', variant)}
      role="radio"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Box {...tileStyles.tileImageArea} className="cover-swatch-grid__tile-image-area">
        {variant.swatchImageUrl ? (
          <Image
            alt={fallbackLabel}
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            src={variant.swatchImageUrl}
            {...tileStyles.tileImage}
          />
        ) : (
          <CoverSwatchImageNotAvailable
            svgProps={tileStyles.tileImage}
            wrapperProps={tileStyles.tileImagePlaceholder}
          />
        )}

        {badgeNode}

        {isOutOfStock && (
          <Box
            className="cover-swatch-grid__tile-oos-overlay-line"
            {...tileStyles.tileOOSOverlayLine}
          />
        )}
      </Box>

      <Box {...tileStyles.tileLabelArea} className="cover-swatch-grid__tile-label-area">
        {nameLine1 && (
          <Text
            noOfLines={1}
            {...tileStyles.tileNameLine1}
            className="cover-swatch-grid__tile-name-line1"
          >
            {nameLine1}
          </Text>
        )}
        {nameLine2 && (
          <Text
            noOfLines={2}
            {...tileStyles.tileNameLine2}
            className="cover-swatch-grid__tile-name-line2"
          >
            {nameLine2}
          </Text>
        )}
        {isMobile && onViewDetails && (
          <Text
            as="span"
            className="cover-swatch-grid__tile-view-details"
            {...tileStyles.tileViewDetails}
            onClick={handleViewDetails}
          >
            <FormattedMessage
              defaultMessage="See details"
              id="cover_swatch_grid.view_details"
            />
          </Text>
        )}
        {stockMessageText && (
          <Text {...tileStyles[stockMessageStyleKey]} className={stockMessageClassName}>
            {stockMessageText}
          </Text>
        )}
      </Box>
    </Box>
  );
}

CoverTile.propTypes = {
  variant: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  isMobile: PropTypes.bool,
  styles: PropTypes.object,
  masterMinPrice: PropTypes.number,
  currency: PropTypes.string,
  onSelect: PropTypes.func,
  onViewDetails: PropTypes.func,
  hideStockLabels: PropTypes.bool,
};

export function CoverSwatchGrid({
  variants,
  selectedCoverId,
  masterMinPrice,
  currency,
  onSelect,
  onMoreClick,
  onViewDetails,
  title,
  hideStockLabels = false,
}) {
  const intl = useIntl();
  const gridTitleId = useId();
  const isMobile = useBreakpointValue({base: true, lg: false});
  const visibleTileCap =
    useBreakpointValue(VISIBLE_TILE_CAP_BY_BREAKPOINT, {fallback: 'base'}) ??
    DEFAULT_VISIBLE_TILE_CAP;
  const styles = useMultiStyleConfig('CoverSwatchGrid', {isSelected: false, isOutOfStock: false});
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  // Sticky promotion of an overflow cover (freeform "blink" fix). When the shopper picks
  // a cover that isn't in the default first-7 (via "+N more"), we promote it to slot 1 so
  // it stays visible. That promotion must PERSIST when they subsequently pick an
  // already-visible tile — otherwise the previously promoted swatch snaps back to the
  // overflow bucket and appears to "blink"/disappear. `pinnedCoverId` holds that promoted
  // overflow cover independently of the live `selectedCoverId`.
  const [pinnedCoverId, setPinnedCoverId] = useState(null);

  // `pinnedCoverId` drives the row order; fall back to `selectedCoverId` so a deep
  // pre-selected cover (committed before any interaction) is promoted on first paint,
  // before the effect below seeds the pin.
  const promotionCoverId =
    selectedCoverId != null ? pinnedCoverId ?? selectedCoverId : null;
  const {visibleVariants, moreCount} = useMemo(
    () => getCoverSwatchGridVisibleVariants(variants, promotionCoverId, visibleTileCap),
    [variants, promotionCoverId, visibleTileCap]
  );

  // Maintain the sticky promotion in response to selection changes from BOTH sources
  // (grid tiles here + the external "+N more" overflow flyout, which commits via the same
  // `selectedCoverId`). Keyed on `selectedCoverId` so it reacts regardless of origin.
  useEffect(() => {
    const list = Array.isArray(variants) ? variants : [];

    // No overflow → nothing is ever promoted.
    if (list.length <= visibleTileCap) {
      if (pinnedCoverId !== null) setPinnedCoverId(null);

      return;
    }

    if (!selectedCoverId) {
      if (pinnedCoverId !== null) setPinnedCoverId(null);

      return;
    }

    const remainingSlots = visibleTileCap - MORE_CTA_RESERVED_SLOTS;
    const selectedIndex = list.findIndex((variant) => variant.productId === selectedCoverId);
    const isOverflowSelection = selectedIndex >= remainingSlots;
    const isDisplayed = visibleVariants.some((variant) => variant.productId === selectedCoverId);

    if (isOverflowSelection) {
      // Picked an invisible-by-default cover → pin it to slot 1, replacing any prior pin.
      if (pinnedCoverId !== selectedCoverId) setPinnedCoverId(selectedCoverId);
    } else if (!isDisplayed) {
      // Picked a default-region cover that an active pin had pushed out of the row → drop
      // the pin so it becomes visible again (still an "invisible" selection → reposition).
      if (pinnedCoverId !== null) setPinnedCoverId(null);
    }
    // else: picked a currently-visible cover → keep the pin (no reorder, no blink).
  }, [selectedCoverId, variants, visibleVariants, pinnedCoverId, visibleTileCap]);

  const handleMoreClick = useCallback(() => {
    if (typeof onMoreClick === 'function') {
      onMoreClick();

      return;
    }
    setIsOverflowOpen(true);
  }, [onMoreClick]);

  const handleOverflowSelect = useCallback((variant) => {
    onSelect?.(variant);
    setIsOverflowOpen(false);
  }, [onSelect]);

  if (!variants?.length) return null;

  const resolvedTitle = title ?? intl.formatMessage({
    defaultMessage: 'Choose your cover',
    id: 'cover_swatch_grid.title.choose_your_cover',
  });

  return (
    <Box {...styles.root} className="cover-swatch-grid">
      <Box {...styles.header} className="cover-swatch-grid__header">
        <Text id={gridTitleId} {...styles.title}>{resolvedTitle}</Text>
      </Box>

      <SimpleGrid
        aria-labelledby={gridTitleId}
        role="radiogroup"
        {...styles.grid}
        className="cover-swatch-grid__grid"
      >
        {visibleVariants.map((variant) => (
          <CoverTile
            key={variant.productId}
            currency={currency}
            hideStockLabels={hideStockLabels}
            isMobile={isMobile}
            isSelected={variant.productId === selectedCoverId}
            masterMinPrice={masterMinPrice}
            styles={styles}
            variant={variant}
            onSelect={onSelect}
            onViewDetails={onViewDetails}
          />
        ))}

        {moreCount > 0 && (
          <Box
            {...styles.moreCta}
            className="cover-swatch-grid__more-cta"
            role="button"
            tabIndex={0}
            aria-label={intl.formatMessage(
              {
                defaultMessage: 'Show {count} more covers',
                id: 'cover_swatch_grid.more_cta.aria_label',
              },
              {count: moreCount}
            )}
            onClick={handleMoreClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleMoreClick();
              }
            }}
          >
            <Box {...styles.moreCtaImageArea} className="cover-swatch-grid__more-cta-image-area">
              <ColorPaletteSampleIcon {...styles.moreCtaIcon} className="cover-swatch-grid__more-cta-icon" />
            </Box>
            <Box {...styles.moreCtaLabel} className="cover-swatch-grid__more-cta-label">
              <Box {...styles.moreCtaCtaRow} className="cover-swatch-grid__more-cta-cta-row">
                <Text {...styles.moreCtaCount} className="cover-swatch-grid__more-cta-count">
                  <FormattedMessage
                    defaultMessage="View all"
                    id="cover_swatch_grid.more_cta.view_all"
                  />
                </Text>
                <ChevronRightIcon2
                  {...styles.moreCtaCaret}
                  aria-hidden
                  className="cover-swatch-grid__more-cta-caret"
                />
              </Box>
              <Text {...styles.moreCtaMore} className="cover-swatch-grid__more-cta-more">
                <FormattedMessage
                  defaultMessage="{count}+ colours"
                  id="cover_swatch_grid.more_cta.colours"
                  values={{count: Array.isArray(variants) ? variants.length : moreCount}}
                />
              </Text>
            </Box>
          </Box>
        )}
      </SimpleGrid>

      <Text {...styles.description} className="cover-swatch-grid__description">
        <FormattedMessage
          defaultMessage="Colour reproduction may vary on different monitors, please order a swatch before placing an order."
          id="cover_swatch_grid.description"
        />
      </Text>

      {/* Overflow drawer for variants beyond the 8-tile cap (FSD §5.3).
          Only used when no external `onMoreClick` is provided. */}
      {moreCount > 0 && !onMoreClick && (
        <Modal
          isOpen={isOverflowOpen}
          scrollBehavior="inside"
          size={{base: 'full', lg: 'lg'}}
          onClose={() => setIsOverflowOpen(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{resolvedTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <SimpleGrid columns={{base: 3, lg: 4}} spacing={2}>
                {variants.map((variant) => (
                  <CoverTile
                    key={`overflow-${variant.productId}`}
                    currency={currency}
                    hideStockLabels={hideStockLabels}
                    isMobile={isMobile}
                    isSelected={variant.productId === selectedCoverId}
                    masterMinPrice={masterMinPrice}
                    styles={styles}
                    variant={variant}
                    onSelect={handleOverflowSelect}
                    onViewDetails={onViewDetails}
                  />
                ))}
              </SimpleGrid>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

CoverSwatchGrid.displayName = 'CoverSwatchGrid';

CoverSwatchGrid.propTypes = {
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string.isRequired,
      name: PropTypes.string,
      swatchName: PropTypes.string,
      swatchImageUrl: PropTypes.string,
      swatchDescription: PropTypes.string,
      orderable: PropTypes.bool,
      ats: PropTypes.number,
      perpetual: PropTypes.bool,
      variationValues: PropTypes.object,
      salePrice: PropTypes.number,
      price: PropTypes.number,
      productName: PropTypes.string,
      c_processedData: PropTypes.shape({
        materialName: PropTypes.string,
        productName: PropTypes.string,
        badgeText: PropTypes.string,
      }),
    })
  ),
  selectedCoverId: PropTypes.string,
  masterMinPrice: PropTypes.number,
  hideStockLabels: PropTypes.bool,
  currency: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onMoreClick: PropTypes.func,
  onViewDetails: PropTypes.func,
  title: PropTypes.string,
};

export default CoverSwatchGrid;
